/**
 * Der Abruf über eine Bankschnittstelle, sichtbar gemacht.
 *
 * Das Wichtigste an dieser Ansicht ist nicht der Knopf „Jetzt abrufen",
 * sondern die Zeile darüber: wie lange die Zustimmung noch gilt. Sie
 * läuft nach spätestens 90 Tagen ab, und wenn das unbemerkt passiert,
 * sieht der stehengebliebene Abruf genauso aus wie ein Monat ohne
 * Kontobewegung — bis beim Jahresabschluss ein Vierteljahr fehlt.
 *
 * Deshalb wird gewarnt, bevor es passiert, und der letzte Fehlversuch
 * bleibt sichtbar, statt in einem Protokoll zu verschwinden.
 */
import { useState, type ReactNode } from 'react';
import { Dialog } from '../bausteine/Dialog';
import { Auswahlfeld, Textfeld } from '../bausteine/Feld';
import { Marker } from '../bausteine/Marker';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import { useAbruf, useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import { datumZeit } from '../lib/format';
import type { AbrufBericht, Bankkonto, Verfahren, ZugangStand } from '../lib/typen';

export function Zugangskarte({
  konto,
  darfSchreiben,
  nachAbruf,
}: {
  konto: Bankkonto;
  darfSchreiben: boolean;
  nachAbruf: () => void;
}): ReactNode {
  const stand = useAbruf<ZugangStand>(`/api/bankkonten/${konto.id}/zugang`);
  const vorgang = useVorgang();
  const [einrichten, setzeEinrichten] = useState(false);
  const [scaUrl, setzeScaUrl] = useState<string | null>(null);
  const [bericht, setzeBericht] = useState<AbrufBericht | null>(null);
  const [tan, setzeTan] = useState('');

  /*
   * Die TAN nachreichen.
   *
   * Nur bei FinTS. Die Bank fragt mitten im Abruf, und der Timer um
   * Viertel nach sechs kann niemanden fragen — er hinterlässt die Frage,
   * und hier wird sie beantwortet.
   */
  async function reicheTanNach(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden<AbrufBericht>(`/api/bankkonten/${konto.id}/zugang/tan`, { tan: tan.trim() }),
    );
    if (ergebnis !== undefined) {
      setzeBericht(ergebnis);
      setzeTan('');
      stand.neuLaden();
    }
  }

  if (!stand.daten) return null;

  async function fordereZustimmung(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden<{ scaUrl: string | null }>(`/api/bankkonten/${konto.id}/zugang/zustimmung`),
    );
    if (ergebnis !== undefined) {
      setzeScaUrl(ergebnis.scaUrl);
      stand.neuLaden();
    }
  }

  async function pruefe(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden(`/api/bankkonten/${konto.id}/zugang/pruefen`),
    );
    if (ergebnis !== undefined) {
      setzeScaUrl(null);
      stand.neuLaden();
    }
  }

  async function rufeAb(): Promise<void> {
    setzeBericht(null);
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden<AbrufBericht>(`/api/bankkonten/${konto.id}/zugang/abruf`),
    );
    if (ergebnis !== undefined) {
      setzeBericht(ergebnis);
      nachAbruf();
    }
    /*
     * Auch nach einem Fehlschlag neu laden.
     *
     * Verlangt die Bank eine TAN, ist das kein Erfolg — die Frage steht
     * aber im Zugang, und nur ein Neuladen bringt sie in die Anzeige.
     * Ohne das bliebe die Aufforderung unsichtbar, und der Benutzer sähe
     * nur, dass etwas fehlgeschlagen ist.
     */
    stand.neuLaden();
  }

  async function beende(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.entfernen(`/api/bankkonten/${konto.id}/zugang`),
    );
    if (ergebnis !== undefined) stand.neuLaden();
  }

  // --- Noch nicht eingerichtet ------------------------------------------
  if (!stand.daten.eingerichtet) {
    if (!darfSchreiben) return null;
    return (
      <>
        <div className="klein" style={{ marginTop: '0.5rem' }}>
          <button type="button" className="knopf klein" onClick={() => setzeEinrichten(true)}>
            Abruf einrichten
          </button>
        </div>
        {einrichten ? (
          <EinrichtenDialog
            konto={konto}
            schliessen={() => setzeEinrichten(false)}
            fertig={() => {
              setzeEinrichten(false);
              stand.neuLaden();
            }}
          />
        ) : null}
      </>
    );
  }

  const zugang = stand.daten;
  const laeuftAb =
    zugang.zustimmungStatus === 'gueltig' &&
    zugang.tageBisAblauf !== null &&
    zugang.tageBisAblauf <= 14;

  return (
    <div style={{ marginTop: '0.625rem', borderTop: '1px solid var(--rand)', paddingTop: '0.5rem' }}>
      <div className="reihe verteilt">
        <span className="klein still">
          {zugang.verfahren === 'fints'
            ? `FinTS · BLZ ${zugang.fintsBlz ?? '—'}`
            : `Abruf über ${zugang.anbieter}`}
        </span>
        {zugang.verfahren === 'fints' ? null : <Marker status={zugang.zustimmungStatus} />}
      </div>

      {zugang.verfahren !== 'fints' && zugang.zustimmungStatus === 'gueltig' && zugang.zustimmungBis ? (
        <div className="klein" style={laeuftAb ? { color: 'var(--warnung)' } : { color: 'var(--text-still)' }}>
          {laeuftAb
            ? `Zustimmung läuft in ${zugang.tageBisAblauf} Tagen ab (${zugang.zustimmungBis}) — jetzt erneuern.`
            : `Zustimmung gilt bis ${zugang.zustimmungBis} (${zugang.tageBisAblauf} Tage).`}
        </div>
      ) : null}

      {zugang.letzterAbrufAm ? (
        <div className="klein still">
          Letzter Abruf: {datumZeit(zugang.letzterAbrufAm)}
          {zugang.letzterAbrufOk === false ? ' — fehlgeschlagen' : ''}
        </div>
      ) : (
        <div className="klein still">Noch nie abgerufen.</div>
      )}

      {/* Bei FinTS gehört das Handy zum Vorgang. Wer das nicht weiß,
          drückt auf „Jetzt abrufen", wartet und hält den Vorgang für
          hängengeblieben, während die Freigabe auf dem Sperrbildschirm
          liegt. */}
      {zugang.verfahren === 'fints' ? (
        <div className="klein still">
          Verlangt die Bank eine Freigabe, erscheint sie in der Banking-App
          {zugang.fintsTanVerfahren ? ` (${zugang.fintsTanVerfahren})` : ''}. Der Abruf
          wartet dann bis zu zweieinhalb Minuten darauf.
        </div>
      ) : null}

      {/* Der letzte Fehler bleibt stehen, bis ein Abruf gelingt. Ein Timer
          um Viertel nach sieben hat niemanden, dem er etwas melden könnte. */}
      {zugang.letzterFehler ? (
        <div style={{ marginTop: '0.5rem' }}>
          <Meldung ton="gefahr">{zugang.letzterFehler}</Meldung>
        </div>
      ) : null}

      <Fehlermeldung text={vorgang.fehler} />

      {scaUrl ? (
        <div style={{ marginTop: '0.5rem' }}>
          <Meldung ton="info" titel="Zustimmung bestätigen.">
            Im Online-Banking bestätigen, danach hier auf „Prüfen" drücken.
            <div style={{ marginTop: '0.375rem' }}>
              <a href={scaUrl} target="_blank" rel="noreferrer">
                Zum Online-Banking
              </a>
            </div>
          </Meldung>
        </div>
      ) : null}

      {bericht ? (
        <div style={{ marginTop: '0.5rem' }}>
          <Meldung
            ton={bericht.erfolg ? (bericht.neu > 0 ? 'gut' : 'info') : 'gefahr'}
            titel={
              bericht.erfolg
                ? `${bericht.gelesen} Umsätze gelesen.`
                : 'Der Abruf ist fehlgeschlagen.'
            }
          >
            {bericht.erfolg
              ? `${bericht.neu} neu, ${bericht.duplikate} bereits vorhanden (${bericht.zeitraum.von} bis ${bericht.zeitraum.bis}).`
              : bericht.fehler}
            {bericht.hinweise.length > 0 ? (
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                {bericht.hinweise.map((h) => (
                  <li key={h} className="klein">
                    {h}
                  </li>
                ))}
              </ul>
            ) : null}
          </Meldung>
        </div>
      ) : null}

      {/* Wartet ein Abruf auf eine TAN, ist das die einzige Handlung, die
          gerade weiterhilft — deshalb steht sie vor allen Knöpfen. */}
      {zugang.tanOffen && darfSchreiben ? (
        <div style={{ marginTop: '0.5rem' }}>
          <Meldung ton="warnung" titel="Die Bank verlangt eine TAN.">
            {zugang.tanOffen.hinweis ?? 'Bitte im gewohnten Verfahren freigeben.'}
            <div className="reihe" style={{ marginTop: '0.5rem', gap: '0.375rem' }}>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={tan}
                placeholder="TAN"
                onChange={(e) => setzeTan(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
              <button
                type="button"
                className="knopf klein haupt"
                disabled={tan.trim() === '' || vorgang.laeuft}
                onClick={() => void reicheTanNach()}
              >
                TAN senden
              </button>
            </div>
            <div className="klein still" style={{ marginTop: '0.375rem' }}>
              Die Aufforderung gilt nur wenige Minuten. Läuft sie ab, hilft ein
              neuer Abruf — die Bank stellt dann eine neue.
            </div>
          </Meldung>
        </div>
      ) : null}

      {darfSchreiben ? (
        <div className="reihe" style={{ marginTop: '0.5rem', gap: '0.375rem', flexWrap: 'wrap' }}>
          {/* Bei FinTS gibt es keine Zustimmung, die im Browser erteilt
              wird: Die starke Authentifizierung passiert bei der
              Anmeldung. Also führt hier nur ein Weg weiter — abrufen. */}
          {zugang.verfahren === 'fints' ? (
            <button
              type="button"
              className="knopf klein haupt"
              disabled={vorgang.laeuft}
              onClick={() => void rufeAb()}
            >
              {vorgang.laeuft ? 'Warte auf Freigabe …' : 'Jetzt abrufen'}
            </button>
          ) : zugang.zustimmungStatus === 'gueltig' ? (
            <>
              <button
                type="button"
                className="knopf klein haupt"
                disabled={vorgang.laeuft}
                onClick={() => void rufeAb()}
              >
                Jetzt abrufen
              </button>
              {laeuftAb ? (
                <button
                  type="button"
                  className="knopf klein"
                  disabled={vorgang.laeuft}
                  onClick={() => void fordereZustimmung()}
                >
                  Zustimmung erneuern
                </button>
              ) : null}
            </>
          ) : zugang.zustimmungStatus === 'angefragt' ? (
            <>
              <button
                type="button"
                className="knopf klein haupt"
                disabled={vorgang.laeuft}
                onClick={() => void pruefe()}
              >
                Prüfen
              </button>
              <button
                type="button"
                className="knopf klein"
                disabled={vorgang.laeuft}
                onClick={() => void fordereZustimmung()}
              >
                Neu anfordern
              </button>
            </>
          ) : (
            <button
              type="button"
              className="knopf klein haupt"
              disabled={vorgang.laeuft}
              onClick={() => void fordereZustimmung()}
            >
              Zustimmung anfordern
            </button>
          )}

          <button
            type="button"
            className="knopf klein"
            disabled={vorgang.laeuft}
            onClick={() => setzeEinrichten(true)}
          >
            Einstellungen
          </button>

          {zugang.aktiv ? (
            <button
              type="button"
              className="knopf klein"
              disabled={vorgang.laeuft}
              onClick={() => void beende()}
            >
              Abruf beenden
            </button>
          ) : null}
        </div>
      ) : null}

      {zugang.letzteAbrufe.length > 0 ? (
        <details style={{ marginTop: '0.5rem' }}>
          <summary className="klein still">Letzte Abrufe</summary>
          <ul style={{ margin: '0.375rem 0 0', paddingLeft: '1.25rem' }}>
            {zugang.letzteAbrufe.map((a) => (
              <li key={a.id} className="klein still">
                {datumZeit(a.begonnenAm)} — {a.erfolg ? 'erfolgreich' : `Fehler${a.httpStatus ? ` ${a.httpStatus}` : ''}`}
                {a.ausgeloest === 'timer' ? ' (automatisch)' : ''}
                {a.fehler ? <div className="nebentext">{a.fehler}</div> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {einrichten ? (
        <EinrichtenDialog
          konto={konto}
          vorhanden={zugang}
          schliessen={() => setzeEinrichten(false)}
          fertig={() => {
            setzeEinrichten(false);
            stand.neuLaden();
          }}
        />
      ) : null}
    </div>
  );
}

// =========================================================================
//  Einrichten
// =========================================================================

function EinrichtenDialog({
  konto,
  vorhanden,
  schliessen,
  fertig,
}: {
  konto: Bankkonto;
  vorhanden?: {
    anbieter: string;
    basisUrl: string;
    institutKennung: string | null;
    abrufAb: string | null;
    verfahren?: Verfahren;
    fintsBlz?: string | null;
    fintsBenutzerkennung?: string | null;
    fintsProduktId?: string | null;
    fintsTanVerfahren?: string | null;
    pinHinterlegt?: boolean;
  };
  schliessen: () => void;
  fertig: () => void;
}): ReactNode {
  const vorgang = useVorgang();
  const [verfahren, setzeVerfahren] = useState<Verfahren>(vorhanden?.verfahren ?? 'fints');
  const [anbieter, setzeAnbieter] = useState(vorhanden?.anbieter ?? '');
  const [basisUrl, setzeBasisUrl] = useState(vorhanden?.basisUrl ?? '');
  const [institut, setzeInstitut] = useState(vorhanden?.institutKennung ?? '');
  const [abrufAb, setzeAbrufAb] = useState(vorhanden?.abrufAb ?? '');
  const [token, setzeToken] = useState('');
  const [blz, setzeBlz] = useState(vorhanden?.fintsBlz ?? '');
  const [kennung, setzeKennung] = useState(vorhanden?.fintsBenutzerkennung ?? '');
  const [pin, setzePin] = useState('');
  const [produktId, setzeProduktId] = useState(vorhanden?.fintsProduktId ?? '');
  const [tanVerfahren, setzeTanVerfahren] = useState(vorhanden?.fintsTanVerfahren ?? '');

  const istFints = verfahren === 'fints';

  // Was mindestens ausgefüllt sein muss, damit Speichern nicht in einen
  // 400er läuft. Die Regel steht auch in der API — hier steht sie, damit
  // der Knopf grau bleibt statt zu scheitern.
  const fertigAusgefuellt = istFints
    ? blz.trim().length === 8 &&
      kennung.trim() !== '' &&
      basisUrl.trim() !== '' &&
      (pin.trim() !== '' || Boolean(vorhanden?.pinHinterlegt))
    : anbieter.trim() !== '' && basisUrl.trim() !== '';

  async function speichern(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.ersetzen(`/api/bankkonten/${konto.id}/zugang`, {
        verfahren,
        // Bei FinTS gibt es keinen Anbieter im Sinne eines Dienstleisters —
        // man spricht mit der Bank selbst. Der Name der Bank ist das, was
        // in der Anzeige Sinn ergibt.
        anbieter: anbieter.trim() || (istFints ? 'FinTS' : ''),
        basisUrl: basisUrl.trim(),
        ...(institut.trim() ? { institutKennung: institut.trim() } : {}),
        ...(abrufAb ? { abrufAb } : {}),
        ...(token.trim() ? { zugriffToken: token.trim() } : {}),
        ...(istFints
          ? {
              fintsBlz: blz.trim(),
              fintsBenutzerkennung: kennung.trim(),
              ...(pin.trim() ? { fintsPin: pin.trim() } : {}),
              fintsProduktId: produktId.trim() || null,
              fintsTanVerfahren: tanVerfahren.trim() || null,
            }
          : {}),
      }),
    );
    if (ergebnis !== undefined) fertig();
  }

  return (
    <Dialog
      titel={`Abruf für ${konto.bezeichnung}`}
      schliessen={schliessen}
      fuss={
        <>
          <button type="button" className="knopf" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf haupt"
            disabled={!fertigAusgefuellt || vorgang.laeuft}
            onClick={() => void speichern()}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stapel">
        <Fehlermeldung text={vorgang.fehler} />

        {konto.iban ? null : (
          <Meldung ton="warnung" titel="Diesem Konto fehlt die IBAN.">
            Die Zustimmung wird auf die IBAN ausgestellt — ohne sie geht der Abruf nicht.
          </Meldung>
        )}

        {/* Die Wahl steht ganz oben, weil sie bestimmt, welche Felder
            darunter überhaupt Sinn ergeben. */}
        <Auswahlfeld<Verfahren>
          beschriftung="Verfahren"
          wert={verfahren}
          setzeWert={setzeVerfahren}
          optionen={[
            { wert: 'fints', text: 'FinTS/HBCI — direkt mit der Bank' },
            { wert: 'berlin_group', text: 'Berlin Group / giroAPI — über einen Anbieter' },
          ]}
        />

        {istFints ? (
          <Meldung ton="info" titel="Was hier hineingehört.">
            Dieselben Angaben wie im Online-Banking: Bankleitzahl,
            Benutzerkennung und PIN. Dazu die FinTS-Adresse der Bank — sie
            steht in deren Hilfe zum Thema „Zugang für Finanzsoftware".
            Gelesen werden nur Umsätze; Überweisungen kann diese Software
            nicht auslösen.
          </Meldung>
        ) : (
          <Meldung ton="info" titel="Was hier hineingehört.">
            Die Adresse der Schnittstelle nach dem openFinance-Framework der Berlin
            Group, ohne das <code>/v1</code> am Ende. Sie kommt vom Anbieter — bei
            giroAPI vom Teilnehmer, über den der Zugang läuft.
          </Meldung>
        )}

        <div className="formularraster">
          {istFints ? (
            <>
              <Textfeld
                beschriftung="Bankleitzahl"
                wert={blz}
                setzeWert={setzeBlz}
                maxLaenge={8}
                klasse="zahl"
                pflicht
                hinweis="Acht Ziffern."
              />
              <Textfeld
                beschriftung="Benutzerkennung"
                wert={kennung}
                setzeWert={setzeKennung}
                pflicht
                hinweis="Wie beim Anmelden im Online-Banking."
              />
              <Textfeld
                beschriftung="PIN"
                art="password"
                wert={pin}
                setzeWert={setzePin}
                pflicht={!vorhanden?.pinHinterlegt}
                hinweis={
                  vorhanden?.pinHinterlegt
                    ? 'Hinterlegt. Nur ausfüllen, um sie zu ersetzen — angezeigt wird sie nie.'
                    : 'Wird verschlüsselt gespeichert und nie wieder angezeigt.'
                }
              />
              <Textfeld
                beschriftung="Produkt-ID"
                wert={produktId}
                setzeWert={setzeProduktId}
                maxLaenge={25}
                hinweis="25-stellige Registrierungsnummer der Deutschen Kreditwirtschaft. Volksbanken und Sparkassen verlangen sie."
              />
              <Textfeld
                beschriftung="TAN-Verfahren"
                wert={tanVerfahren}
                setzeWert={setzeTanVerfahren}
                hinweis="Nur zur Erinnerung, etwa SecureGo plus. Ausgewählt wird es von der Bank."
              />
            </>
          ) : (
            <>
              <Textfeld
                beschriftung="Anbieter"
                wert={anbieter}
                setzeWert={setzeAnbieter}
                hinweis="Frei wählbar, erscheint nur in der Anzeige."
              />
              <Textfeld
                beschriftung="Institutskennung"
                wert={institut}
                setzeWert={setzeInstitut}
                hinweis="BIC oder Kennung aus der Institutsliste des Anbieters. Optional."
              />
            </>
          )}

          <Textfeld
            beschriftung={istFints ? 'FinTS-Adresse der Bank' : 'Adresse der Schnittstelle'}
            wert={basisUrl}
            setzeWert={setzeBasisUrl}
            pflicht
            breit
            hinweis={istFints ? 'https://… — der FinTS-Endpunkt des Instituts' : 'https://…  — ohne /v1'}
          />
          <div className="feld">
            <label htmlFor="abrufAb">Abrufen ab</label>
            <input
              id="abrufAb"
              type="date"
              value={abrufAb}
              onChange={(e) => setzeAbrufAb(e.target.value)}
            />
            <span className="hinweis">
              Meist der Beginn des Geschäftsjahres. Ohne Angabe: 1. Januar.
            </span>
          </div>
          {istFints ? null : (
            <Textfeld
              beschriftung="Zugriffstoken"
              wert={token}
              setzeWert={setzeToken}
              hinweis={
                vorhanden
                  ? 'Nur ausfüllen, um ein neues Token zu hinterlegen. Das vorhandene wird nicht angezeigt.'
                  : 'Nur bei Anbietern, die ein festes Token ausgeben. Wird verschlüsselt gespeichert.'
              }
            />
          )}
        </div>
      </div>
    </Dialog>
  );
}
